import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import EncabezadoSST from './EncabezadoSST';
import SeccionPreguntasSST from './SeccionPreguntasSST';
import { useFormularioDinamico } from '../../../../hooks/useFormularioDinamico';

// ==========================================
// COMPONENTE: PIZARRA DE FIRMA (CANVAS)
// ==========================================
function FirmaPad({ onSave, onClear }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#1e293b"; 
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div className="flex flex-col items-center gap-3 w-full my-4">
      <div className="bg-white/60 backdrop-blur-md border border-slate-300 rounded-2xl shadow-inner overflow-hidden w-full max-w-[400px] h-[180px] relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute top-3 left-4 text-[10px] font-black text-slate-400 uppercase tracking-widest pointer-events-none select-none">
          Firma del Evaluador
        </div>
      </div>
      <button 
        onClick={handleClear}
        type="button"
        className="text-[10px] uppercase tracking-wider font-black text-slate-500 hover:text-red-500 transition-colors cursor-pointer bg-white/50 px-4 py-1.5 rounded-full border border-slate-200 shadow-sm"
      >
        Limpiar Firma
      </button>
    </div>
  );
}

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.setAttribute("data-portal-modal", "true");
    div.setAttribute("translate", "no");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

export default function InspeccionSST({ usuario, onBack }) {
  const nombreInspector = usuario?.nombres || usuario?.nombre || '';

  // Conectamos con el motor lógico estandarizado y ABAC
  const {
    formulario, respuestas: datosServer, loading, enviando, sedes,
    handleChange: handleServerChange, handleSubmit
  } = useFormularioDinamico('inspeccion-sst', usuario);

  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    if (!datosServer.fecha) handleServerChange('fecha', new Date().toISOString().split('T')[0]);
    if (!datosServer.inspector && nombreInspector) handleServerChange('inspector', nombreInspector);
    if (!datosServer.unidadNegocio) handleServerChange('unidadNegocio', 'EL GRAN SURTIDOR');
  }, [datosServer.fecha, datosServer.inspector, datosServer.unidadNegocio, handleServerChange, nombreInspector]);

  // Extraer las secciones parametrizadas desde la BD (Cero hardcode)
  const campoConfig = formulario?.campos?.find(c => c.nombre_campo === 'respuestas_inspeccion')?.opciones || {};
  const seccionesInspeccion = campoConfig.secciones || [];
  const seccionMantenimiento = campoConfig.mantenimiento || { titulo: "", icono: "", preguntas: [] };

  const totalPreguntas = seccionesInspeccion.reduce((acc, seccion) => acc + (seccion.preguntas?.length || 0), 0);

  const respuestas = datosServer.respuestas || {};
  const observaciones = datosServer.observaciones || {};
  const evidenciasFotos = datosServer.evidenciasFotos || {};

  const handleResponder = (seccion, idPregunta, valor) => {
    const nuevasRespuestas = { ...respuestas, [idPregunta]: valor };
    handleServerChange('respuestas', nuevasRespuestas);
  };

  const handleObservacion = (seccion, idPregunta, texto) => {
    const nuevasObs = { ...observaciones, [idPregunta]: texto };
    handleServerChange('observaciones', nuevasObs);
  };

  const handleFotoPregunta = (idPregunta, base64) => {
    const nuevasFotos = { ...evidenciasFotos, [idPregunta]: base64 };
    handleServerChange('evidenciasFotos', nuevasFotos);
  };

  // Cálculo de estadísticas dinámico según las preguntas devueltas por la BD
  const estadisticas = useMemo(() => {
    let totalResp = 0;
    let totalPuntos = 0;
    const promediosPorSeccion = {};

    seccionesInspeccion.forEach(seccion => {
      let sumaSeccion = 0;
      let cuentaSeccion = 0;

      if (seccion.preguntas) {
        seccion.preguntas.forEach(p => {
          if (respuestas[p.id] !== undefined) {
            sumaSeccion += respuestas[p.id];
            cuentaSeccion++;
            totalResp++;
            totalPuntos += respuestas[p.id];
          }
        });
      }

      promediosPorSeccion[seccion.nombre] = cuentaSeccion > 0 ? (sumaSeccion / cuentaSeccion) * 100 : 0;
    });

    return {
      porcentajeGeneral: totalResp > 0 ? (totalPuntos / totalResp) * 100 : 0,
      promedios: promediosPorSeccion
    };
  }, [respuestas, seccionesInspeccion]);

  // Actualizar el puntaje global para que se guarde en la BD principal
  useEffect(() => {
    if (estadisticas.porcentajeGeneral > 0) {
      handleServerChange('puntaje_global', estadisticas.porcentajeGeneral);
    }
  }, [estadisticas.porcentajeGeneral, handleServerChange]);

  const guardarLocal = (e) => {
    if (!datosServer.pdv || !datosServer.pdv.trim()) {
      return setCustomAlert({ show: true, message: "Por favor, selecciona el Punto de Venta (PDV).", type: "warning" });
    }
    if (Object.keys(respuestas).length === 0) {
      return setCustomAlert({ show: true, message: "No has respondido ninguna pregunta de la inspección.", type: "warning" });
    }
    if (!datosServer.firma_evaluador) {
      return setCustomAlert({ show: true, message: "La firma del evaluador es obligatoria para guardar el formulario.", type: "warning" });
    }

    handleSubmit(e, () => {
      setCustomAlert({ show: true, message: "¡Inspección guardada exitosamente!", type: "success" });
      setTimeout(() => { onBack && onBack(); }, 1500);
    });
  };

  if (loading || !formulario) return <div className="p-10 text-center text-white font-bold">Cargando Inspección SG-SST...</div>;

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      {/* CABECERA */}
      <div className="bg-transparent border-b border-white/20 px-4 py-3 sm:px-8 sm:py-5 z-40 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button onClick={onBack} className="w-10 h-10 flex-none bg-white/60 hover:bg-white/80 text-slate-700 hover:text-red-500 flex items-center justify-center rounded-full transition-all shadow-sm border border-white/40 cursor-pointer backdrop-blur-sm" title="Cerrar Formulario">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <h1 className="text-lg sm:text-xl font-black text-slate-800 leading-tight">{formulario.titulo}</h1>
            <span className="bg-white/60 text-blue-800 border border-white/50 text-[10px] font-bold px-2 py-0.5 rounded-md backdrop-blur-sm">FT-SST-06 | V-{formulario.version}</span>
          </div>
        </div>
        
        <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl p-2 px-5 shadow-inner flex items-center justify-between gap-4 text-white w-full sm:w-auto border border-slate-700/50">
          <div className="text-center">
            <p className="text-[8px] text-slate-300 font-bold uppercase tracking-widest mb-0.5">Avance</p>
            <p className="text-xs sm:text-sm font-black text-blue-400">
              {Object.keys(respuestas).filter(k => !k.startsWith("rep_mant_")).length} <span className="text-slate-500 text-[10px]">/ {totalPreguntas}</span>
            </p>
          </div>
          <div className="w-px h-6 bg-slate-600/50"></div>
          <div className="text-center">
            <p className="text-[8px] text-slate-300 font-bold uppercase tracking-widest mb-0.5">% Cumpl</p>
            <p className={`text-xs sm:text-sm font-black leading-none ${estadisticas.porcentajeGeneral >= 90 ? 'text-emerald-400' : estadisticas.porcentajeGeneral >= 70 ? 'text-yellow-400' : 'text-red-400'}`}>
              {estadisticas.porcentajeGeneral.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-8 w-full relative">
        {/* COMPONENTE ENCABEZADO ORIGINAL (Adaptado al objeto datosServer) */}
        <EncabezadoSST datos={datosServer} setDatos={(nuevosDatos) => {
          Object.keys(nuevosDatos).forEach(k => handleServerChange(k, nuevosDatos[k]));
        }} listaSedes={sedes} />

        <div className="space-y-4">
          {seccionesInspeccion.map((info) => (
            <div key={info.nombre} className="relative mt-2">
              <div className="absolute -top-3 right-4 z-10 bg-slate-800 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-md border border-slate-600">
                {(estadisticas.promedios[info.nombre] || 0).toFixed(1)}%
              </div>
              
              <SeccionPreguntasSST 
                titulo={info.nombre} 
                icono={info.icono}
                preguntas={info.preguntas || []}
                respuestas={respuestas}
                observaciones={observaciones}
                evidenciasFotos={evidenciasFotos}
                onResponder={handleResponder}
                onObservacion={handleObservacion}
                onFotoPregunta={handleFotoPregunta}
              />
            </div>
          ))}

          {/* SECCIÓN MANTENIMIENTO (Opcional) */}
          {seccionMantenimiento && seccionMantenimiento.preguntas && seccionMantenimiento.preguntas.length > 0 && (
            <div className="relative mt-6 bg-white/70 backdrop-blur-xl rounded-[28px] border border-white/80 shadow-md p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200/60 pb-4 mb-5 gap-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{seccionMantenimiento.icono}</span>
                  <div>
                    <h3 className="font-black text-slate-800 text-base uppercase tracking-wider">{seccionMantenimiento.titulo}</h3>
                    <p className="text-xs text-slate-500 font-bold mt-0.5">Módulo opcional e informativo (No afecta el porcentaje general)</p>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-black uppercase tracking-wider shrink-0 w-fit">Opcional / Novedades</span>
              </div>

              <div className="space-y-4">
                {seccionMantenimiento.preguntas.map((item) => {
                  const estadoMantenimiento = respuestas[item.id]; 
                  const tieneNovedad = estadoMantenimiento === "NO_CUMPLE";
                  const fotoActual = evidenciasFotos[item.id];

                  return (
                    <div key={item.id} className="bg-white/90 rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-2xs space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <span className="text-xs font-black text-slate-800 flex-1">{item.texto}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleResponder("MANTENIMIENTO_NOVEDADES", item.id, "CUMPLE")}
                            className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer ${estadoMantenimiento === "CUMPLE" ? "bg-emerald-600 text-white shadow-md scale-105" : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"}`}
                          >
                            ✓ Sin Novedad / Bueno
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResponder("MANTENIMIENTO_NOVEDADES", item.id, "NO_CUMPLE")}
                            className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer ${estadoMantenimiento === "NO_CUMPLE" ? "bg-amber-600 text-white shadow-md scale-105" : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200"}`}
                          >
                            ⚠ Reportar Novedad
                          </button>
                        </div>
                      </div>

                      {tieneNovedad && (
                        <div className="pt-3 mt-3 border-t border-slate-200/80 space-y-3 animate-fade-in">
                          <textarea
                            rows="2"
                            placeholder="Describa detalladamente la novedad de mantenimiento..."
                            value={observaciones[item.id] || ""}
                            onChange={(e) => handleObservacion("MANTENIMIENTO_NOVEDADES", item.id, e.target.value)}
                            className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-inner resize-none"
                          />

                          <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">📸</span>
                              <span className="text-[11px] font-bold text-slate-600">{fotoActual ? "Fotografía de evidencia adjunta ✅" : "Adjuntar registro fotográfico de la novedad"}</span>
                            </div>
                            
                            <label className="px-4 py-2 bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white rounded-lg text-xs font-black transition cursor-pointer border border-blue-200 shadow-sm">
                              <span>{fotoActual ? "Cambiar Foto" : "Subir Foto"}</span>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onloadend = () => handleFotoPregunta(item.id, reader.result);
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                            </label>
                          </div>

                          {fotoActual && (
                            <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-300 shadow-sm">
                              <img src={fotoActual} alt="Evidencia Novedad" className="w-full h-full object-cover" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* FIRMA Y BOTÓN GUARDAR */}
        <div className="mt-8 border-t border-white/20 pt-8 pb-4 flex flex-col items-center">
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-2">Validación y Cierre</h3>
          <p className="text-xs text-slate-600 font-medium mb-4 text-center">Por favor, registra tu firma en el recuadro inferior para certificar la inspección.</p>
          <FirmaPad onSave={(firma) => handleServerChange('firma_evaluador', firma)} onClear={() => handleServerChange('firma_evaluador', null)} />
          <button onClick={guardarLocal} disabled={enviando} className="w-full max-w-sm mt-6 bg-gradient-to-r from-blue-600/90 to-blue-500/90 hover:from-blue-600 hover:to-blue-500 backdrop-blur-md text-white font-black py-4 rounded-xl shadow-[0_8px_30px_rgba(37,99,235,0.3)] active:scale-95 transition-all text-sm tracking-widest uppercase cursor-pointer border border-blue-400/50 disabled:opacity-50">
            {enviando ? 'Guardando...' : 'Guardar Inspección'}
          </button>
        </div>
      </div>

      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 backdrop-blur-md ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : customAlert.type === 'warning' ? 'Atención' : 'Error'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className="w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer bg-blue-600 hover:bg-blue-500">Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}